<template>
  <section class="timeline-panel" v-loading="loading">
    <header class="timeline-panel__head">
      <h2 class="timeline-panel__title">项目时间线</h2>

      <div class="timeline-panel__actions">
        <button
          type="button"
          class="timeline-panel__toggle"
          :aria-expanded="expanded ? 'true' : 'false'"
          :aria-label="expanded ? '收起时间线' : '展开时间线'"
          :title="expanded ? '收起时间线' : '展开时间线'"
          @click="expanded = !expanded"
        >
          <span class="timeline-panel__chevron" :class="{ 'timeline-panel__chevron--expanded': expanded }" aria-hidden="true"></span>
        </button>
      </div>
    </header>

    <div class="timeline-facts">
      <article class="timeline-fact">
        <span>创建时间</span>
        <strong>{{ formatDateTime(createdAt) }}</strong>
      </article>
      <article class="timeline-fact">
        <span>最近审批</span>
        <strong>{{ formatDateTime(latestApprovalAt) }}</strong>
      </article>
      <article class="timeline-fact">
        <span>最近更新</span>
        <strong>{{ formatDateTime(latestUpdatedAt) }}</strong>
      </article>
    </div>

    <div v-if="expanded && orderedItems.length" class="timeline-list">
      <article v-for="item in orderedItems" :key="item.id" class="timeline-entry">
        <div class="timeline-entry__rail">
          <span class="timeline-entry__dot" :class="`timeline-entry__dot--${eventTone(item)}`"></span>
          <span class="timeline-entry__line"></span>
        </div>

        <div class="timeline-entry__card">
          <div class="timeline-entry__top">
            <div>
              <strong>{{ item.title }}</strong>
              <p>{{ formatDateTime(item.timestamp) }}</p>
            </div>
            <div class="timeline-entry__tags">
              <el-tag size="small" effect="plain" :type="eventTagType(item)">{{ eventLabel(item) }}</el-tag>
              <el-tag v-if="item.stage" size="small" effect="plain">{{ stageLabel(item.stage) }}</el-tag>
            </div>
          </div>

          <p v-if="item.message" class="timeline-entry__message">{{ item.message }}</p>

          <div v-if="item.actor?.name" class="timeline-entry__meta">
            <span>操作人</span>
            <strong>{{ item.actor.name }}</strong>
            <em v-if="item.actor.role">{{ item.actor.role }}</em>
          </div>

          <div v-if="approvalFeedback(item)" class="timeline-entry__feedback">
            <span>审批意见</span>
            <p>{{ approvalFeedback(item) }}</p>
          </div>

          <div v-if="reconfirmedFromLabel(item)" class="timeline-entry__feedback timeline-entry__feedback--info">
            <span>回退来源</span>
            <p>{{ reconfirmedFromLabel(item) }}</p>
          </div>
        </div>
      </article>
    </div>

    <el-empty v-else-if="expanded" description="暂时还没有可展示的项目时间记录" />
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { ProjectTimelineItem } from '@/types/projectTimeline'

const props = defineProps<{
  loading: boolean
  items: ProjectTimelineItem[]
  createdAt?: string | null
  latestUpdatedAt?: string | null
}>()

const expanded = ref(false)

const orderedItems = computed(() => {
  return [...props.items].sort((left, right) => {
    return new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime()
  })
})

const latestApprovalAt = computed(() => {
  const latest = orderedItems.value.find((item) => ['approval_approved', 'approval_rejected'].includes(item.event_type))
  return latest?.timestamp || null
})

function formatDateTime(value?: string | null) {
  if (!value) {
    return '暂无'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function stageLabel(stage?: string | null) {
  const labels: Record<string, string> = {
    proposal: '方案',
    script: '剧本',
    storyboard: '分镜',
    final: '成片',
    demand_analysis: '需求分析',
    asset_generation: '素材',
    video_synthesis: '视频',
    compliance_check: '合规',
  }
  return labels[String(stage || '')] || String(stage || '')
}

function eventLabel(item: ProjectTimelineItem) {
  const labels: Record<string, string> = {
    project_created: '创建',
    workflow_started: '启动',
    approval_requested: '待审批',
    approval_approved: '已通过',
    approval_rejected: '已退回',
    revision_requested: '修订',
    upstream_reconfirmed: '重确认',
    stage_completed: '阶段完成',
    workflow_completed: '完成',
  }
  return labels[item.event_type] || item.event_type
}

function eventTagType(item: ProjectTimelineItem) {
  if (item.event_type === 'approval_approved' || item.event_type === 'workflow_completed') return 'success'
  if (item.event_type === 'approval_rejected' || item.event_type === 'revision_requested') return 'danger'
  if (item.event_type === 'upstream_reconfirmed') return 'warning'
  if (item.event_type === 'approval_requested') return 'warning'
  return 'info'
}

function eventTone(item: ProjectTimelineItem) {
  if (item.event_type === 'approval_approved' || item.event_type === 'workflow_completed') return 'success'
  if (item.event_type === 'approval_rejected' || item.event_type === 'revision_requested') return 'danger'
  if (item.event_type === 'upstream_reconfirmed') return 'warning'
  if (item.event_type === 'approval_requested') return 'warning'
  return 'neutral'
}

function approvalFeedback(item: ProjectTimelineItem) {
  return String(item.metadata?.feedback || item.metadata?.reason || '').trim()
}

function reconfirmedFromLabel(item: ProjectTimelineItem) {
  const source = String(item.metadata?.reconfirmed_from || '').trim()
  if (!source) {
    return ''
  }
  return `从${stageLabel(source)}阶段回退，改为基于当前${stageLabel(item.stage)}重新生成后续内容。`
}
</script>

<style scoped>
.timeline-panel {
  display: grid;
  gap: 16px;
  margin-bottom: 22px;
  padding: 20px 22px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top right, rgba(14, 165, 233, 0.1), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 22px 48px rgba(15, 23, 42, 0.07);
}

.timeline-panel__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.timeline-panel__title {
  color: #0f172a;
  font-size: 20px;
  line-height: 1.2;
}

.timeline-facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(140px, 1fr));
  gap: 12px;
}

.timeline-panel__actions {
  display: flex;
  justify-content: flex-end;
}

.timeline-panel__toggle {
  appearance: none;
  -webkit-appearance: none;
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  align-items: center;
  padding: 0;
  border-radius: 999px;
  border: 1px solid rgba(191, 219, 254, 0.72);
  background: rgba(255, 255, 255, 0.9);
  color: #1e3a8a;
  cursor: pointer;
  box-shadow: none;
  outline: none;
}

.timeline-panel__toggle:hover,
.timeline-panel__toggle:active,
.timeline-panel__toggle:focus,
.timeline-panel__toggle:focus-visible {
  border-color: rgba(191, 219, 254, 0.72);
  background: rgba(255, 255, 255, 0.9);
  color: #1e3a8a;
  box-shadow: none;
  outline: none;
}

.timeline-panel__chevron {
  width: 10px;
  height: 10px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg) translate(-1px, -1px);
  transition: transform 0.18s ease;
}

.timeline-panel__chevron--expanded {
  transform: rotate(225deg) translate(-1px, -1px);
}

.timeline-fact {
  display: grid;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(191, 219, 254, 0.5);
}

.timeline-fact span {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.timeline-fact strong {
  color: #0f172a;
  font-size: 15px;
  line-height: 1.5;
}

.timeline-list {
  display: grid;
  gap: 0;
}

.timeline-entry {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 16px;
}

.timeline-entry:last-child .timeline-entry__line {
  opacity: 0;
}

.timeline-entry__rail {
  display: grid;
  justify-items: center;
  grid-template-rows: 18px 1fr;
}

.timeline-entry__dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 3px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 0 1px rgba(148, 163, 184, 0.18);
}

.timeline-entry__dot--success {
  background: #10b981;
}

.timeline-entry__dot--warning {
  background: #f59e0b;
}

.timeline-entry__dot--danger {
  background: #ef4444;
}

.timeline-entry__dot--neutral {
  background: #64748b;
}

.timeline-entry__line {
  width: 2px;
  margin-top: 6px;
  background: linear-gradient(180deg, rgba(148, 163, 184, 0.4), rgba(226, 232, 240, 0.15));
}

.timeline-entry__card {
  display: grid;
  gap: 12px;
  padding: 0 0 24px;
}

.timeline-entry__top {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.timeline-entry__top strong {
  color: #0f172a;
  font-size: 17px;
}

.timeline-entry__top p,
.timeline-entry__message,
.timeline-entry__feedback p {
  margin-top: 6px;
  color: #64748b;
  line-height: 1.7;
}

.timeline-entry__tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.timeline-entry__meta,
.timeline-entry__feedback {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.timeline-entry__meta span,
.timeline-entry__feedback span {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.timeline-entry__meta strong {
  color: #0f172a;
}

.timeline-entry__meta em {
  color: #475569;
  font-style: normal;
}

.timeline-entry__feedback {
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(248, 250, 252, 0.92);
  border: 1px solid rgba(226, 232, 240, 0.9);
  align-items: flex-start;
}

.timeline-entry__feedback--info {
  background: rgba(239, 246, 255, 0.92);
  border-color: rgba(191, 219, 254, 0.95);
}

.timeline-entry__feedback p {
  margin: 0;
  color: #334155;
}

@media (max-width: 1080px) {
  .timeline-panel__head {
    align-items: flex-start;
  }

  .timeline-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .timeline-panel {
    padding: 18px;
  }

  .timeline-facts {
    grid-template-columns: 1fr;
  }

  .timeline-entry__top {
    flex-direction: column;
  }

  .timeline-entry__tags {
    justify-content: flex-start;
  }
}
</style>